<svelte:options runes={true} />

<script lang="ts">
  import { Slider } from "bits-ui";
  import type { SliderRootSnippetProps } from "bits-ui";
  import type { Component } from "svelte";
  import type { ClassValue } from "svelte/elements";

  // Cast to bypass the discriminated union — our Props interface handles the constraints
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const SliderRoot = Slider.Root as Component<any>;

  interface Props {
    type: "single" | "multiple";
    value?: number | number[];
    min?: number;
    max?: number;
    step?: number;
    disabled?: boolean;
    orientation?: "horizontal" | "vertical";
    showThumbLabels?: boolean;
    labelPosition?: "top" | "bottom" | "left" | "right";
    labelFormatter?: (value: number) => string;
    thumbLabel?: (index: number, value: number) => string;
    onChange?: (value: number | number[]) => void;
    onCommit?: (value: number | number[]) => void;
    class?: ClassValue;
  }

  let {
    type,
    value = $bindable(type === "multiple" ? [] : 0),
    min = 0,
    max = 100,
    step = 1,
    disabled,
    orientation = "horizontal",
    showThumbLabels = false,
    labelPosition = "bottom",
    labelFormatter,
    thumbLabel,
    onChange,
    onCommit,
    class: className = "",
  }: Props = $props();

  function defaultThumbLabel(index: number): string {
    return type === "multiple" ? `Value ${index + 1}` : "Value";
  }
</script>

<SliderRoot
  {type}
  {value}
  {min}
  {max}
  {step}
  {disabled}
  {orientation}
  onValueChange={(v: number[]) => {
    value = v;
    onChange?.(v);
  }}
  onValueCommit={onCommit}
  class={[
    "da-slider",
    `da-slider--${orientation}`,
    showThumbLabels && "da-slider--has-thumb-labels",
    className,
  ]}
>
  {#snippet children({ thumbItems }: SliderRootSnippetProps)}
    <Slider.Range class="da-slider__range" />
    {#each thumbItems as thumb (thumb.index)}
      <Slider.Thumb
        index={thumb.index}
        class="da-slider__thumb"
        aria-label={(thumbLabel ?? defaultThumbLabel)(thumb.index, thumb.value)}
      />
      {#if showThumbLabels}
        <Slider.ThumbLabel
          index={thumb.index}
          position={labelPosition}
          class="da-slider__thumb-label"
        >
          {labelFormatter ? labelFormatter(thumb.value) : thumb.value}
        </Slider.ThumbLabel>
      {/if}
    {/each}
  {/snippet}
</SliderRoot>

<style>
  @layer aurora {
    :global(.da-slider) {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;
      height: var(--da-slider-track-height, 8px);
      background: var(--da-slider-track-bg, var(--da-color-light-sand-700));
      border-radius: var(--da-radius-lg);
      margin: var(--da-space-4) 0;
      user-select: none;
    }

    :global(.da-slider__range) {
      position: absolute;
      height: 100%;
      background: var(--da-slider-range-bg, var(--da-color-brand-sand-900));
      border-radius: var(--da-radius-lg);
    }

    :global(.da-slider__thumb) {
      display: block;
      width: var(--da-slider-thumb-size, 20px);
      height: var(--da-slider-thumb-size, 20px);
      background: var(--da-slider-thumb-bg, var(--da-color-white));
      border: 1px solid var(--da-slider-thumb-border, var(--da-color-charcoal-50));
      border-radius: 50%;
      box-shadow: var(--da-shadow-sm);
      cursor: grab;
      outline: none;
      flex-shrink: 0;
    }

    :global(.da-slider__thumb[data-active]) {
      cursor: grabbing;
    }

    :global(.da-slider__thumb:focus-visible) {
      outline: 2px solid var(--da-color-charcoal-500);
      outline-offset: 2px;
    }

    :global(.da-slider__thumb-label) {
      font-family: var(--da-font-family-primary);
      font-size: var(--da-slider-label-size, var(--da-font-size-sm));
      font-weight: var(--da-font-weight-medium);
      color: var(--da-slider-label-color, var(--da-color-charcoal-900));
      white-space: nowrap;
      pointer-events: none;
      padding: var(--da-slider-label-padding-block, var(--da-space-4))
        var(--da-slider-label-padding-inline, var(--da-space-1));
    }

    :global(.da-slider--has-thumb-labels) {
      margin-inline: var(--da-space-2);
      margin-bottom: var(--da-space-8);
      width: calc(100% - var(--da-space-4));
    }
  }
</style>
